import * as React from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useOverlay } from "@/hooks/use-overlay";
import { Button } from "@/components/ui/button";
import { useEffect, useId } from "react";
import { setRuntimeDirty } from "@/runtime/dirty";

interface SheetProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  size?: "md" | "lg";
  footer?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

export function Sheet({
  open,
  onClose,
  title,
  description,
  size = "md",
  footer,
  children,
  className,
}: SheetProps) {
  const runtimeDirtyId = useId();
  useEffect(() => {
    setRuntimeDirty(runtimeDirtyId, open);
    return () => setRuntimeDirty(runtimeDirtyId, false);
  }, [runtimeDirtyId, open]);
  useOverlay(open, onClose);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0 bg-overlay backdrop-blur-sm animate-overlay-in"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        className={cn(
          "absolute inset-y-0 right-0 flex h-full w-full flex-col border-l border-border bg-card shadow-modal animate-sheet-in",
          size === "lg" ? "max-w-[640px]" : "max-w-[480px]",
          className
        )}
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-border px-6 py-4">
          <div>
            <h2 className="text-base font-semibold tracking-tight text-foreground">
              {title}
            </h2>
            {description && (
              <p className="mt-0.5 text-[13px] text-muted-foreground">
                {description}
              </p>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Close"
            className="h-8 w-8 shrink-0"
            onClick={onClose}
          >
            <X size={16} />
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 scrollbar-thin">
          {children}
        </div>
        {footer && (
          <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border bg-card px-6 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
